// Sign-up on Firebase (SPEC-14), in three steps:
//   1. Firebase Auth creates the user and opens the session.
//   2. setupAccount (functions repo) sets the accountId claim that
//      firestore.rules and the data callables read; the ID token is then
//      refreshed so it carries the claim.
//   3. saveProfile stores the first name in Dados gerais.
//
// Each step can fail on its own (the network drops, the 10 s run out).
// Run again with the same email, the flow resumes: the user this page
// created is reused, the claim is set once, and the profile is written
// whole. A retry never meets "Já existe uma conta" for an account it
// created itself.

import { dataLayer } from "../data";
import { loadFirebase } from "../lib/firebaseModules";
import { ensureAccountClaim } from "./accountClaim";
import { codeOf } from "./firebaseError";
import type { SignUp } from "./signUp";
import { SignUpError } from "./signUp";

/**
 * The uid this page created, in memory only. A retry resumes that account
 * and no other: an account found signed in on a shared phone is not this
 * sign-up's to change, and its password and Dados gerais stay untouched.
 */
let createdUid: string | null = null;

export const firebaseSignUp: SignUp = async ({ firstName, email, password }) => {
  try {
    // Never fetched offline, so a tap without a network leaves the next one
    // free to load them (src/lib/firebaseModules.ts).
    const [{ app, auth }, authApi] = await loadFirebase();

    let user = auth.currentUser;
    if (user && user.uid === createdUid && user.email === email) {
      // A retry after a step below failed: the password typed now wins.
      await authApi.updatePassword(user, password);
    } else {
      user = (await authApi.createUserWithEmailAndPassword(auth, email, password)).user;
      createdUid = user.uid;
    }

    await ensureAccountClaim(app, user);

    await dataLayer().saveProfile({ firstName });
  } catch (error) {
    const code = codeOf(error);
    // Firebase keeps answering email-already-in-use with email enumeration
    // protection on (poweryourflow-functions, test/account.test.ts).
    if (code === "auth/email-already-in-use") throw new SignUpError("email-in-use");
    if (code === "auth/invalid-email") throw new SignUpError("email-invalid");
    console.error("[sign-up] failed", { code });
    throw new SignUpError("failed");
  }
};
